import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AlertCircle, Check, ChevronDown, ChevronRight, CircleDashed, CircleHelp, Info, Lock, Minus, X, type LucideProps } from "lucide-react";
import { cn } from "@nexus/ui";
import type { AskNexusToolActivity as ToolActivity, AskNexusToolStatus } from "@/features/ask-nexus/api/ask-nexus.api";
import { toolContext, toolLabel } from "@/features/ask-nexus/lib/tool-labels";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";

const STATUS_STROKE = 2.5;
/** The server sends the start of each result; a preview this long was cut. */
const RESULT_PREVIEW_CHARS = 600;

/** `waiting`: no result yet, drawn as an empty outline rather than a filled mark. */
type Tone = "pos" | "neg" | "neutral" | "waiting";

/** How each status looks and what the person is told the call got back. */
const STATUS_STYLE: Record<Exclude<AskNexusToolStatus, "running">, { icon: ComponentType<LucideProps>; tone: Tone; key: string; fallback: string }> = {
  succeeded: { icon: Check, tone: "pos", key: "askNexus.activity.status.succeeded", fallback: "Done" },
  pending: { icon: CircleDashed, tone: "waiting", key: "askNexus.activity.status.pending", fallback: "Still being prepared, no data yet" },
  unavailable: { icon: X, tone: "neg", key: "askNexus.activity.status.unavailable", fallback: "Nexus did not answer" },
  needs_input: { icon: Info, tone: "neutral", key: "askNexus.activity.status.needsInput", fallback: "Needed more details, no data" },
  denied: { icon: Lock, tone: "neg", key: "askNexus.activity.status.denied", fallback: "Not available to this chat" },
  failed: { icon: X, tone: "neg", key: "askNexus.activity.status.failed", fallback: "Failed" },
  not_finished: { icon: Minus, tone: "neutral", key: "askNexus.activity.status.notFinished", fallback: "Did not finish" },
  unrecognized: { icon: CircleHelp, tone: "neutral", key: "askNexus.activity.status.unrecognized", fallback: "Result not recognized, treated as no data" },
};

const TONE_CLASSES: Record<Tone, string> = {
  pos: "bg-(--an-pos-tint) text-(--an-pos-ink)",
  neg: "bg-(--an-neg-tint) text-(--an-neg-ink)",
  neutral: "bg-(--an-neutral-tint) text-(--an-ink-2)",
  waiting: "text-(--an-ink-3)",
};

/** The Nexus tools the agent used for one answer, and what each one actually returned. */
export function AskNexusToolActivity({ activity, isRunning }: { activity: ToolActivity[]; isRunning: boolean }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(isRunning);
  if (activity.length === 0) return null;

  const isAllSucceeded = activity.every((item) => item.status === "succeeded");
  return (
    <div className="overflow-hidden rounded-2xl border border-(--an-hair-strong)" data-testid="ask-nexus-tool-activity">
      <button
        type="button"
        className="flex h-10 w-full items-center gap-2 pr-3 pl-3.5 text-left text-[13px] text-(--an-ink-2) hover:bg-(--an-inset)"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {isRunning ? (
          <span className="ask-nexus-spin size-4 flex-none" aria-hidden="true" />
        ) : isAllSucceeded ? (
          <StatusMark status="succeeded" />
        ) : (
          <AlertCircle className="size-4 flex-none text-(--an-ink-2)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1 truncate">{isRunning ? t("askNexus.activity.reading", "Reading Nexus data") : summary(t, activity)}</span>
        <ChevronDown
          className={cn("size-3.5 flex-none text-(--an-ink-3) transition-transform", isOpen && "rotate-180")}
          strokeWidth={ASK_NEXUS_ICON_STROKE}
          aria-hidden="true"
        />
      </button>
      {isOpen ? (
        <ul className="flex flex-col gap-0.5 border-t border-(--an-hair) p-1.5">
          {activity.map((item) => (
            <ToolRow key={item.id} item={item} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** "Used 5 Nexus tools", then what did not return data, so a pending call never reads as data retrieved. */
function summary(t: TFunction, activity: ToolActivity[]): string {
  const count = (status: AskNexusToolStatus) => activity.filter((item) => item.status === status).length;
  const parts = [
    t("askNexus.activity.used", { count: activity.length, defaultValue: "Used {{count}} Nexus tools" }),
    ...([
      ["pending", "askNexus.activity.summary.pending", "{{count}} still being prepared"],
      ["unavailable", "askNexus.activity.summary.unavailable", "{{count}} unavailable"],
      ["needs_input", "askNexus.activity.summary.needsInput", "{{count}} needed more details"],
      ["denied", "askNexus.activity.summary.denied", "{{count}} not available"],
      ["failed", "askNexus.activity.summary.failed", "{{count}} failed"],
      ["not_finished", "askNexus.activity.summary.notFinished", "{{count}} did not finish"],
      ["unrecognized", "askNexus.activity.summary.unrecognized", "{{count}} not recognized"],
    ] as const).flatMap(([status, key, fallback]) => {
      const n = count(status);
      return n > 0 ? [t(key, fallback, { count: n })] : [];
    }),
  ];
  return parts.join(" · ");
}

function ToolRow({ item }: { item: ToolActivity }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const context = toolContext(item.input);
  const style = item.status === "running" ? null : STATUS_STYLE[item.status];

  return (
    <li className={cn("rounded-xl", isOpen && "bg-(--an-inset)")}>
      <button
        type="button"
        className={cn(
          "flex min-h-9 w-full items-center gap-2.5 rounded-xl py-1.5 pr-2 pl-2 text-left text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--an-ink-3)",
          !isOpen && "hover:bg-(--an-inset)",
        )}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <StatusMark status={item.status} />
        <span className="min-w-0 flex-1">
          <span className={cn(style?.tone === "neg" && "text-(--an-neg-ink)")}>
            {toolLabel(t, item.tool)}
            {item.status === "running" ? "…" : ""}
          </span>
          {context ? <span className="block truncate text-xs text-(--an-ink-2)">{context}</span> : null}
          {style && item.status !== "succeeded" ? (
            <span className="block text-xs text-(--an-ink-2)">{t(style.key, style.fallback)}</span>
          ) : null}
        </span>
        <ChevronRight
          className={cn("size-3.5 flex-none text-(--an-ink-3) transition-transform", isOpen && "rotate-90")}
          strokeWidth={ASK_NEXUS_ICON_STROKE}
          aria-hidden="true"
        />
      </button>
      {isOpen ? (
        // Indented to the row's label (8px padding + 18px mark + 10px gap), on the row's own background.
        <div className="pr-3 pb-2.5 pl-9 text-xs leading-[1.55] text-(--an-ink-2)">
          <p>
            <span className="font-medium text-(--an-ink)">{t("askNexus.activity.askedFor", "Asked for:")}</span>{" "}
            {context || t("askNexus.activity.noInputs", "No filters, the default for this organization")}
          </p>
          <p>
            <span className="font-medium text-(--an-ink)">{t("askNexus.activity.gotBack", "Got back:")}</span> {gotBack(t, item)}
          </p>
          {item.resultPreview ? (
            <>
              <button
                type="button"
                className="mt-1.5 text-xs font-medium text-(--an-ink) underline decoration-(--an-hair-dashed) underline-offset-2 hover:decoration-current"
                aria-expanded={showRaw}
                onClick={() => setShowRaw((open) => !open)}
              >
                {showRaw ? t("askNexus.activity.hideRaw", "Hide raw result") : t("askNexus.activity.showRaw", "Show raw result")}
              </button>
              {showRaw ? (
                <>
                  <pre className="mt-1.5 max-h-40 overflow-auto rounded-lg bg-(--an-field) px-2.5 py-2 font-mono text-[11px] break-words whitespace-pre-wrap">
                    {item.resultPreview}
                  </pre>
                  {item.resultPreview.length >= RESULT_PREVIEW_CHARS ? (
                    <p className="mt-1 text-[11px] text-(--an-ink-3)">
                      {t("askNexus.activity.rawCut", "First {{count}} characters only.", { count: RESULT_PREVIEW_CHARS })}
                    </p>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** What the call returned, in words; never a value from the result. */
function gotBack(t: TFunction, item: ToolActivity): string {
  if (item.status === "running") return t("askNexus.activity.gotBackRunning", "Nothing yet, still running");
  if (item.status !== "succeeded") return t(STATUS_STYLE[item.status].key, STATUS_STYLE[item.status].fallback);
  const result = item.gotBack;
  if (!result || result.counts.length === 0) return t("askNexus.activity.gotBackReply", "A reply from Nexus");
  const parts = result.counts.map(({ kind, count }) => t(`askNexus.activity.counts.${kind}`, { count, defaultValue: "{{count}} items" }));
  if (result.startDate && result.endDate) parts.push(`${result.startDate} → ${result.endDate}`);
  if (result.source === "owned_accounts") parts.push(t("askNexus.activity.source.owned", "from your connected ad accounts"));
  if (result.source === "public_ad_library") parts.push(t("askNexus.activity.source.library", "from the public ad library"));
  return parts.join(" · ");
}

function StatusMark({ status }: { status: AskNexusToolStatus }) {
  const { t } = useTranslation();
  // Every mark takes the same 18px, so labels start in one column whatever the status.
  if (status === "running") {
    return (
      <span className="grid size-[18px] flex-none place-items-center" role="img" aria-label={t("askNexus.activity.running", "Running")}>
        <span className="ask-nexus-spin size-4" aria-hidden="true" />
      </span>
    );
  }
  const style = STATUS_STYLE[status];
  const MarkIcon = style.icon;
  if (style.tone === "waiting") {
    return (
      <span className={cn("grid size-[18px] flex-none place-items-center", TONE_CLASSES.waiting)} role="img" aria-label={t(style.key, style.fallback)}>
        <MarkIcon className="size-4" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span
      className={cn("grid size-[18px] flex-none place-items-center rounded-full", TONE_CLASSES[style.tone])}
      role="img"
      aria-label={t(style.key, style.fallback)}
    >
      <MarkIcon className="size-[11px]" strokeWidth={STATUS_STROKE} aria-hidden="true" />
    </span>
  );
}
